import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CakePreview from "./CakePreview";
import { useCartStore } from "../stores/cartStore";
import { toast } from "sonner";
import { productApi, flashSaleApi } from '../lib/api';

export default function CakeCustomizer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const addItem = useCartStore((state) => state.addItem);

  const [product, setProduct] = useState(null);
  const [flashSale, setFlashSale] = useState(null);
  const [loading, setLoading] = useState(true);

  const [order, setOrder] = useState({
    baseCake: "Custom Cake",
    size: "1kg",
    frosting: "Whipped Cream",
    fillings: [],
    toppings: [],
    decorations: [],
    customMessage: ""
  });

  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const [productRes, flashSalesRes] = await Promise.all([
          productApi.getById(id),
          flashSaleApi.getAll(true),
        ]);
        const prod = productRes.data;
        setProduct(prod);

        const firstSize = prod.weight_options?.[0]?.size || "1kg";
        setOrder((prev) => ({
          ...prev,
          baseCake: prod.name,
          size: firstSize,
          frosting: prod.default_frosting || "Whipped Cream"
        }));

        const sale = flashSalesRes.data.find((f) => f.product_id === id);
        setFlashSale(sale);
      } catch (error) {
        console.error("Error loading product:", error);
        toast.error("Product not found");
        navigate("/products");
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  useEffect(() => {
    const buildPrompt = () => {
      let parts = [
        `A ${order.size} ${order.baseCake} with ${order.frosting} frosting`
      ];
      if (order.fillings.length) parts.push(`filled with ${order.fillings.join(", ")}`);
      if (order.toppings.length) parts.push(`topped with ${order.toppings.join(", ")}`);
      if (order.decorations.length) parts.push(`decorated with ${order.decorations.join(", ")}`);
      if (order.customMessage) parts.push(`and a custom message saying "${order.customMessage}"`);
      return parts.join(", ") + ".";
    };
    setPrompt(buildPrompt());
  }, [order]);

  const handleChange = (field, value) => {
    setOrder((prev) => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setOrder((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleAddToCart = () => {
    const price = calculatePrice(order, product, flashSale);
    addItem({
      id: product.id,
      name: `${order.baseCake} (${order.size})`,
      price,
      image: product.images?.[0] || "",
      size: order.size,
      frosting: order.frosting,
      fillings: order.fillings,
      toppings: order.toppings,
      decorations: order.decorations,
      customMessage: order.customMessage
    });
    toast.success(`${order.baseCake} added to cart!`);
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-stone-500">
        Loading customization options...
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-center text-[#ff4d4d]">
        Customize Your Cake
      </h2>

      {/* Size Selector */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Size</label>
        {product.weight_options?.map((opt) => (
          <label key={opt.size} className="inline-flex items-center mr-4">
            <input
              type="radio"
              name="size"
              value={opt.size}
              checked={order.size === opt.size}
              onChange={() => handleChange("size", opt.size)}
              className="mr-2"
            />
            {opt.size} @ KSh.{opt.price}
          </label>
        ))}
      </div>

      {/* Frosting Selector */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Frosting</label>
        {[order.frosting, "Whipped Cream", "Buttercream", "Fondant"]
          .filter((f, i, arr) => arr.indexOf(f) === i)
          .map((frosting) => (
            <label key={frosting} className="inline-flex items-center mr-4">
              <input
                type="radio"
                name="frosting"
                value={frosting}
                checked={order.frosting === frosting}
                onChange={() => handleChange("frosting", frosting)}
                className="mr-2"
              />
              {frosting}
            </label>
          ))}
      </div>

      {/* Fillings, Toppings, Decorations */}
      {["fillings", "toppings", "decorations"].map((field) =>
        product.addons?.[field]?.length ? (
          <div key={field} className="mb-4">
            <label className="block font-semibold mb-1 capitalize">{field}</label>
            {product.addons[field].map((item) => (
              <label key={item.name} className="inline-flex items-center mr-4 mb-2">
                <input
                  type="checkbox"
                  checked={order[field].includes(item.name)}
                  onChange={() => handleMultiSelect(field, item.name)}
                  className="mr-2"
                />
                {item.name} (+KSh.{item.extra_cost})
              </label>
            ))}
          </div>
        ) : null
      )}

      {/* Custom Message */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Custom Message</label>
        <input
          type="text"
          value={order.customMessage}
          onChange={(e) => handleChange("customMessage", e.target.value)}
          placeholder="e.g. Happy Birthday Stephen!"
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {/* Prompt Box */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Preview Prompt</label>
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full border rounded px-3 py-2"
        />
        <p className="text-sm text-stone-500 mt-1">
          This description is auto‑generated from your selections, but you can edit it.
        </p>
      </div>

      {/* Live Preview */}
      <CakePreview prompt={prompt} productId={product.id} />

      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        className="mt-6 w-full bg-[#ff4d4d] text-white py-3 rounded font-bold hover:bg-red-600"
      >
        Add to Cart – KSh.{calculatePrice(order, product, flashSale)}
      </button>
    </div>
  );
}

function calculatePrice(order, product, flashSale) {
  const sizeOption = product.weight_options?.find((opt) => opt.size === order.size);
  let base = sizeOption?.price || product.price || 2500;

  if (flashSale?.discount_percent) {
    base = base * (1 - flashSale.discount_percent / 100);
  }

  const fillingsCost = product.addons?.fillings
    ?.filter((f) => order.fillings.includes(f.name))
    .reduce((sum, f) => sum + f.extra_cost, 0) || 0;

  const toppingsCost = product.addons?.toppings
    ?.filter((t) => order.toppings.includes(t.name))
    .reduce((sum, t) => sum + t.extra_cost, 0) || 0;

  const decorationsCost = product.addons?.decorations
    ?.filter((d) => order.decorations.includes(d.name))
    .reduce((sum, d) => sum + d.extra_cost, 0) || 0;

  return Math.round(base + fillingsCost + toppingsCost + decorationsCost);
}
